import { useCallback, useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';

import { useBreadcrumbs } from '@hooks/useBreadcrumbs';

import { AuditTrialType } from '@dynamika-shared/types/auditTrial';
import { useAuditTrialsFilters } from './useAuditTrialsFilters';
import { usePaginationAndSort } from '@restore/helpers';

import { convertedDateList, dateRange } from '@utils/DateTime';

import { SubPageHeader } from '@components/SubPageHeader/SubPageHeader';
import {
  DateRangePicker,
  DateRangeType,
} from '@dynamika-shared/ui/DatePicker/DateRangePicker';
import { SearchSuffix } from '@dynamika-shared/ui/Input/SearchSuffix';

import { ChipsFilter } from '@components/ChipsFilter/ChipsFilter';
import { AuditTrialsListPageView } from './AuditTrialsList.view';
import { Dropdown } from '@dynamika-shared/ui/Dropdown/Dropdown';
import ChevronIcon from '@dynamika-shared/ui/icons/Chevron';
import { Input } from '@dynamika-shared/ui/Input/Input';
import { AuditInfo } from './components/AuditInfo';
import { FilledCalendarIcon, FilterDetailedIcon } from '@dynamika-shared/ui/icons';
import { useMakePagination } from '@restore/paginationAndSort/paginationAndSort.slice';
import { SubChips } from 'src/components/ChipsFilter/SubChips';
import { textFormatter } from 'src/utils/textFormatter';
import { useAuditTrialsFiltersSlice } from '@restore/auditTrailsFilters/auditFilters.slice';
import useDebounceFn from '@shared/hooks/useDebouncedValue';
import { formatArray, formatAndFilter } from 'src/utils/auditFiltersHelpers';
import { combineFilterValues } from 'src/utils/combineFilterValues';
import {
  AuditTrialsFetchArgs,
  FilterFetch,
} from '@restore/auditTrailsFilters/auditFilters.types';

type OnSortColumnType = (name: string, dir?: string) => void;

export type AvailableColumnsType = {
  dateAndTime: boolean;
  actionBy: boolean;
  subjectId: boolean;
  visit: boolean;
  businessProcess: boolean;
  event: boolean;
  actionType: boolean;
  field: boolean;
  oldValue: boolean;
  newValue: boolean;
  reasonForModification: boolean;
};

export type GetDataProps = {
  page?: number;
  size?: number;
  sort?: string;
  dates?: DateRangeType | Date[] | string[];
  patientId?: string;
  actionBy?: string;
  businessProcess?: string;
  actionType?: string;
  event?: string;
  clinicalTrialId?: string;
};

export type PayloadProps = Omit<GetDataProps, 'dates'> & {
  dates: string[];
};

export const AuditTrialsList = () => {
  useBreadcrumbs([['Audit Trails', 'auditTrials']], []);

  const [visible, setVisible] = useState(false);
  const [showInfo, setShowInfo] = useState<boolean>(false);
  const [details, setDetails] = useState<Partial<AuditTrialType>>({});
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | undefined>();
  const [openDatePicker, setOpenDatePicker] = useState<boolean>(false);

  const {
    displayFilters,
    selectColumns,
    setDatePeriod,
    date,
    isoDateFormat: selectedDates,
    subFilters,
    filters,
    allFilters,
    toggleSelectedFilter,
    availableSelectedFilters: selectedFilters,
    filterOnClick,
    filteredTableColumns,
    closeSubfilter,
    setActionBySearch,
    actionBySearch,
    search: subjectIdSearch,
    eventSearch,
    setEventSearch,
  } = useAuditTrialsFilters();

  const [data, search, loading, fetch, setSearch, fetchBusinessProcesses] =
    useAuditTrialsFiltersSlice((state, actions) => [
      state.data,
      state.search,
      state.loading,
      actions.fetchAuditTrailList,
      actions.setSearch,
      actions.fetchBusinessProcesses,
    ]);

  const { id: clinicalTrialId } = useParams<{
    id: string;
    taskId: string;
  }>();

  useEffect(() => {
    if (clinicalTrialId) {
      fetchBusinessProcesses({ clinicalTrialId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicalTrialId]);

  const {
    sortBy,
    currentPage,
    currentPageSize,
    setSortBy,
    setCurrentPage: setCurrentAuditPage,
  } = usePaginationAndSort('auditTrials');

  // NOTE: this block dates is default values that we use
  // in order to receive the list of audit trials
  const currentDate = new Date();
  const [thirtyDaysAgoFormatted, nowFormatted] = dateRange(currentDate, 30);
  const datesAsString = convertedDateList(thirtyDaysAgoFormatted, nowFormatted);

  const preparePayload = ({ clinicalTrialId, dates, page, size, sort }: PayloadProps) => {
    const [from, to] = dates;

    return {
      clinicalTrialId: clinicalTrialId ?? '',
      from: from,
      to: to,
      page: page,
      size: size,
      sort: sort,
    };
  };

  const getData = (props: GetDataProps) => {
    const { page, size, sort, dates, ...rest } = props;

    const selectedRange = dates
      ? convertedDateList(dates[0], dates[1])
      : datesAsString;

    const payload = preparePayload({
      clinicalTrialId: clinicalTrialId ?? '',
      dates: selectedRange,
      page,
      size,
      sort,
    });

    const payloadWithFilters = { ...payload, ...(rest ?? {}) };

    fetch(payloadWithFilters);
  };

  const formattedSelectedFilters = formatArray(selectedFilters);

  const sort = !sortBy ? 'dateAndTime,desc' : sortBy;
  const onSort: OnSortColumnType = (name, sortState) => {
    const newSort = sortState ? `${name},${sortState}` : undefined;

    setSortBy({ listName: 'auditTrials', newSortBy: newSort });
  };

  const setDateRange = (dates: DateRangeType) => {
    const [from, to] = dates;
    // NOTE: keep the picker open when the user clears the range
    if (!from || !to) {
      setOpenDatePicker(true);
      return;
    }

    setDatePeriod([from, to]);
    getData({ page: 0, size: currentPageSize, dates });
    setCurrentAuditPage({ listName: 'auditTrials', newPage: 1 });
  };

  const openRowDetails = (
    row: number | Partial<AuditTrialType>,
    rowIndex: number
  ) => {
    const selectedAudit = row as AuditTrialType;
    setDetails(selectedAudit);
    setSelectedRowIndex(rowIndex);
    setShowInfo(true);
  };

  const closeDetailedWindow = () => {
    setShowInfo(false);
    setDetails({});
    setSelectedRowIndex(undefined);
  };

  const filtersFetch = ({ patientCode, actionBy, event }: FilterFetch) => {
    const startDateUTC = date[0]?.toISOString();
    const endDateUTC = date[1]?.toISOString();
    const datesAsString = convertedDateList(startDateUTC, endDateUTC);

    const selectedBusinessProcess = selectedFilters.businessProcess?.join(',');
    const selectedActionType = selectedFilters.actionType?.[0];

    const payload = preparePayload({
      clinicalTrialId: clinicalTrialId ?? '',
      dates: datesAsString,
      page: 0,
      size: currentPageSize,
      sort,
    });

    const alreadySelectedFilters = formatAndFilter({
      actionBy: actionBySearch,
      patientCode: subjectIdSearch,
      event: eventSearch,
      actionType: selectedActionType,
      businessProcess: selectedBusinessProcess,
    });

    const isActionBy = combineFilterValues({
      condition: !!actionBy,
      newFiled: { ...payload, ...alreadySelectedFilters, actionBy: actionBy },
      source: payload,
    });

    const payloadWithPatientCod = combineFilterValues({
      condition: !!patientCode,
      newFiled: {
        ...payload,
        ...alreadySelectedFilters,
        patientCode: patientCode,
      },
      source: isActionBy,
    });

    const payloadWithEvent = combineFilterValues({
      condition: !!event,
      newFiled: { ...payload, ...alreadySelectedFilters, event: event },
      source: payloadWithPatientCod,
    }) as AuditTrialsFetchArgs;

    fetch(payloadWithEvent);
    setCurrentAuditPage({ listName: 'auditTrials', newPage: 1 });
  };

  const { run: delayedSearch } = useDebounceFn((value: string) => {
    const datesAsString = convertedDateList(date[0], date[1]);

    const payload = {
      ...preparePayload({
        clinicalTrialId: clinicalTrialId ?? '',
        dates: datesAsString,
        page: 0,
        size: currentPageSize,
        sort,
      }),
      ...formattedSelectedFilters,
    };

    const payloadWithPatientCod = value ? { ...payload, patientCode: value } : payload;

    // NOTE: actionBySearch and eventSearch live outside selectedFilters,
    // so re-apply them here to keep free-text filters on Subject ID search
    const payloadWithSearches = {
      ...payloadWithPatientCod,
      ...(actionBySearch?.length ? { actionBy: actionBySearch } : {}),
      ...(eventSearch?.length ? { event: eventSearch } : {}),
    };

    fetch(payloadWithSearches);
    setCurrentAuditPage({ listName: 'auditTrials', newPage: 1 });
  }, 1000);

  const todayDate = format(new Date(), 'yyyy-MM-dd');
  const monthDate = format(currentDate.setDate(currentDate.getDate() - 30), 'yyyy-MM-dd');

  const dataContent = useMemo(() => {
    const updatedFields = data?.content?.map(audit => {
      const isFieldNA = audit.field === 'N/A';
      const updatedField = isFieldNA ? audit.field : textFormatter(audit.field);

      const isDXAUploadToWorkStation = audit.businessProcess === 'DXA Upload to Workstation';

      const updatedNewValue =
        isDXAUploadToWorkStation || audit.field === 'patientCode'
          ? audit.newValue
          : textFormatter(audit.newValue);

      const updatedOldValue =
        isDXAUploadToWorkStation || audit.field === 'patientCode'
          ? audit.oldValue
          : textFormatter(audit.oldValue);

      return {
        ...audit,
        field: updatedField,
        oldValue: updatedOldValue,
        newValue: updatedNewValue,
      };
    });

    return { ...data, content: updatedFields };
  }, [data]);

  const shouldHighlightRow = useCallback(
    (row: number | AuditTrialType) => {
      if (typeof row === 'number' || selectedRowIndex === undefined) return;
      return (dataContent?.content as AuditTrialType[])?.[selectedRowIndex] === row;
    },
    [selectedRowIndex, dataContent]
  );

  useEffect(() => {
    const payload = {
      page: currentPage,
      size: currentPageSize,
      sort,
      dates: selectedDates,
    };

    const payloadWithFilters = {
      ...payload,
      ...(formattedSelectedFilters ?? {}),
    };

    const payloadWithSearchPatientCode = subjectIdSearch?.length
      ? {
          ...payloadWithFilters,
          patientCode: subjectIdSearch,
        }
      : payloadWithFilters;

    const payloadWithActonBy = actionBySearch?.length
      ? {
          ...payloadWithSearchPatientCode,
          actionBy: actionBySearch,
        }
      : payloadWithSearchPatientCode;

    const payloadWithEvent = eventSearch?.length
      ? {
          ...payloadWithActonBy,
          event: eventSearch,
        }
      : payloadWithActonBy;

    getData(payloadWithEvent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, currentPageSize, sort, clinicalTrialId, selectedFilters]);

  useEffect(() => {
    return () => {
      setCurrentAuditPage({ listName: 'auditTrials', newPage: 1 });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pagination = useMakePagination('auditTrials', !!dataContent?.content?.length);

  return (
    <div data-testid='ClinicalTrialList' className='h-full'>
      <div className='mt-[1.1rem] flex items-start justify-between [&>header]:mt-0'>
        <DateRangePicker
          data-testid={'auditDatePicker'}
          wrapperClassName='w-[275px] min-h-4'
          prefixIcon={<FilledCalendarIcon className='h-[16px] w-[16px]' />}
          className='h-6 w-[290px] pb-[4px] pl-[30px] pr-[11px] pt-[4px] mini:w-[200px]'
          format={'YYYY-LLL-DD' + ''}
          placeholder={[monthDate, todayDate]}
          suffixIcon={<ChevronIcon dir='down' />}
          value={date}
          onChange={setDateRange}
          open={openDatePicker}
          onOpenChange={() => setOpenDatePicker(prevState => !prevState)}
        />

        <SubPageHeader title='' action={() => {}} className='last:items-start'>
          <ChipsFilter
            onClick={filterOnClick}
            items={filters}
            subFilters={subFilters}
            allFilters={allFilters}
            closeTag={closeSubfilter}
            toggleSelectedFilter={toggleSelectedFilter}
            selectedFilters={selectedFilters}
            overlayClassName={'w-[22.4rem]'}
            setActionBySearch={setActionBySearch}
            setSearch={setSearch}
            setEventSearch={setEventSearch}
            actionBySearch={actionBySearch as string}
            search={subjectIdSearch as string}
            eventSearch={eventSearch as string}
            filtersFetch={filtersFetch}
            showSubFilters={false}
          />
          <Input
            value={search}
            onChange={e => {
              const searchVal = e.target.value.trim();
              setSearch?.(searchVal);
              delayedSearch(searchVal);
            }}
            className='!mt-0 h-[4.8rem] min-w-[32rem] !rounded-[3.2rem] px-2 mini:h-[3.2rem] mini:min-w-[24rem] mini:px-1'
            placeholder='Type Subject ID'
            suffix={
              <SearchSuffix
                disabled={!search}
                action={() => {
                  setSearch?.('');
                  delayedSearch('');
                }}
              />
            }
            data-testid={'auditSearch'}
          />

          <div
            role='button'
            className='flex h-[4rem] cursor-pointer items-center rounded-md hover:bg-waterLight mini:h-[3rem]'
            data-testid={'auditFilter'}
          >
            <Dropdown
              onClick={e => selectColumns(e.key as keyof AvailableColumnsType)}
              items={displayFilters}
              trigger={['click']}
              open={visible}
              className='h-full p-1'
              onOpenChange={(_, { source }) => {
                if (source === 'trigger') setVisible(!visible);
              }}
            >
              <div className='flex items-center justify-center gap-1'>
                <FilterDetailedIcon height={20} width={20} />
                <div className='text-1424'>Display</div>
              </div>
            </Dropdown>
          </div>
        </SubPageHeader>
      </div>

      <SubChips
        subFilters={subFilters}
        allFilters={allFilters}
        actionBySearch={actionBySearch as string}
        selectedFilters={selectedFilters}
        search={search as string}
        eventSearch={eventSearch as string}
        closeTag={closeSubfilter}
        setSearch={setSearch}
        setEventSearch={setEventSearch}
        filtersFetch={filtersFetch}
        setActionBySearch={setActionBySearch}
        toggleSelectedFilter={toggleSelectedFilter}
      />

      <AuditTrialsListPageView
        onSort={onSort}
        sortState={sortBy}
        pagination={pagination}
        loading={loading}
        auditTrialsData={dataContent?.content as AuditTrialType[]}
        totalItems={dataContent?.totalElements ?? 0}
        filteredTableColumns={filteredTableColumns}
        onRowClick={openRowDetails}
        shouldHighlightRow={shouldHighlightRow}
      />

      {showInfo && (
        <AuditInfo open={showInfo} info={details} close={closeDetailedWindow} />
      )}
    </div>
  );
};
